import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  ColorPicker,
  Form,
  Radio,
  Switch,
  Upload,
  message,
} from "antd";
import CropperModal from "../../../Photo/CropperModal";
import { request } from "../../../request";
import {
  brandPalette,
  contrastRatio,
  DEFAULT_BRAND_COLOR,
  isBrandColor,
} from "../../brandColors";
import "./EditPageBranding.css";

/**
 * The "Bluerate page branding" section: the loan officer's own logo and
 * color on their bluerate.ai page (web/docs/plans/bluerate-company-branding.md,
 * section 4). Every field lives under LandingPageSetUp.branding and saves
 * with the rest of the editor; the server checks the values again.
 */

const FIELD = ["LandingPageSetUp", "branding"];

// A cropped logo is scaled to this before upload; the server keeps 512.
const UPLOAD_MAX_SIDE = 1024;

/** Picks a color; reports it only when a drag or a typed value is done. */
function BrandColorInput({
  value,
  onChange,
}: {
  value?: string;
  onChange?(value: string): void;
}) {
  const [shown, setShown] = useState(
    isBrandColor(value) ? value.toUpperCase() : DEFAULT_BRAND_COLOR,
  );
  useEffect(() => {
    if (isBrandColor(value)) setShown(value.toUpperCase());
  }, [value]);

  return (
    <ColorPicker
      value={shown}
      format="hex"
      disabledAlpha
      showText
      presets={[{ label: "Bluerate blue", colors: [DEFAULT_BRAND_COLOR] }]}
      onChange={(color) => setShown(color.toHexString().toUpperCase())}
      onChangeComplete={(color) =>
        onChange?.(color.toHexString().toUpperCase())
      }
    />
  );
}

/** Scales a data: URL image down to UPLOAD_MAX_SIDE on its longer side. */
function scaleDown(dataURL: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(
        1,
        UPLOAD_MAX_SIDE / Math.max(img.width, img.height),
      );
      if (scale === 1) {
        resolve(dataURL);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas
        .getContext("2d")
        ?.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataURL);
    img.src = dataURL;
  });
}

/**
 * The loan officer's own logo: picked, cropped, uploaded straight away, and
 * kept as its address. The editor saves the whole setup on every change, so
 * holding the image itself would upload it again each time.
 */
function OwnLogoInput({
  value,
  onChange,
}: {
  value?: string;
  onChange?(value: string): void;
}) {
  const [imageSrc, setImageSrc] = useState<string>();
  const [uploading, setUploading] = useState(false);

  async function upload(cropped: string) {
    setUploading(true);
    try {
      const res: Response = await request("/los/brandinglogo", {
        method: "POST",
        body: { logo: await scaleDown(cropped) },
        isSuccess: (r) => r.ok,
      });
      const { url } = await res.json();
      onChange?.(url);
    } catch (e) {
      const text = e instanceof Response ? (await e.text()).trim() : "";
      message.error(
        text.replace(/^branding: /, "") ||
          "The logo could not be uploaded. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="page-branding-own-logo">
      {value ? (
        <img src={value} alt="Your logo" />
      ) : (
        <span className="page-branding-muted">No logo uploaded yet</span>
      )}
      <Upload
        accept="image/png,image/jpeg,image/gif"
        showUploadList={false}
        beforeUpload={(file) => {
          const reader = new FileReader();
          reader.onload = () => setImageSrc(String(reader.result));
          reader.readAsDataURL(file);
          return false;
        }}
      >
        <Button loading={uploading}>
          {value ? "Replace logo" : "Upload logo"}
        </Button>
      </Upload>
      <CropperModal
        visible={Boolean(imageSrc)}
        imageSrc={imageSrc}
        onClose={() => setImageSrc(undefined)}
        onSave={upload}
      />
    </div>
  );
}

export interface EditPageBrandingProps {
  /** Where the company logo the page would use loads from; "" when the company has none. */
  companyLogoURL: string;
}

const EditPageBranding: React.FC<EditPageBrandingProps> = ({
  companyLogoURL,
}) => {
  const form = Form.useFormInstance();
  const enabled = Form.useWatch([...FIELD, "enabled"], form);
  const logoSource = Form.useWatch([...FIELD, "logoSource"], form) || "company";
  const ownLogo = Form.useWatch([...FIELD, "logo"], form);
  const color = Form.useWatch([...FIELD, "primaryColor"], form);

  const palette = brandPalette(
    isBrandColor(color) ? color : DEFAULT_BRAND_COLOR,
  );
  const logoURL = logoSource === "own" ? ownLogo : companyLogoURL;
  const onWhite = palette ? contrastRatio(palette.primary, "#FFFFFF") : 21;

  const noLogo =
    logoSource === "own"
      ? "Upload a logo to switch this on."
      : "Your company has no logo yet. Upload your own, or ask your company owner to add one in Company Profile.";

  return (
    <div className="page-branding">
      <p className="page-branding-intro">
        Show your logo at the top of your bluerate.ai page and use your color
        for its buttons, tabs and highlights. Changes save automatically; the
        Bluerate preview shows them once saved.
      </p>

      <Form.Item
        name={[...FIELD, "enabled"]}
        valuePropName="checked"
        label="Use my logo and color on my bluerate.ai page"
        extra={!logoURL ? noLogo : undefined}
      >
        {/* Can always be switched off, only switched on with a logo. */}
        <Switch disabled={!logoURL && !enabled} />
      </Form.Item>

      <Form.Item
        name={[...FIELD, "logoSource"]}
        label="Logo"
        initialValue="company"
      >
        <Radio.Group>
          <Radio value="company">
            <span className="page-branding-option">
              Company logo
              {companyLogoURL ? (
                <img
                  className="page-branding-thumb"
                  src={companyLogoURL}
                  alt="Company logo"
                />
              ) : (
                <span className="page-branding-muted">(none)</span>
              )}
            </span>
          </Radio>
          <Radio value="own">Upload my own</Radio>
        </Radio.Group>
      </Form.Item>

      {logoSource === "own" && (
        <Form.Item name={[...FIELD, "logo"]} noStyle>
          <OwnLogoInput />
        </Form.Item>
      )}

      <Form.Item name={[...FIELD, "primaryColor"]} label="Color">
        <BrandColorInput />
      </Form.Item>

      {palette && onWhite < 3 && (
        <Alert
          className="page-branding-contrast"
          type="warning"
          showIcon
          message={`This color is light: as text on white it reaches only ${onWhite.toFixed(
            1,
          )}:1 (3:1 reads comfortably), so links and outlined buttons may be hard to read. Buttons filled with it get dark text automatically.`}
        />
      )}

      {palette && (
        <div className="page-branding-sample" aria-hidden>
          {logoURL ? (
            <img className="page-branding-sample-logo" src={logoURL} alt="" />
          ) : null}
          <span
            className="page-branding-sample-filled"
            style={{ background: palette.primary, color: palette.onPrimary }}
          >
            Contact
          </span>
          <span
            className="page-branding-sample-outlined"
            style={{ borderColor: palette.primary, color: palette.primary }}
          >
            Pre-approve today
          </span>
          <span
            className="page-branding-sample-selected"
            style={{
              background: palette.tint,
              borderColor: palette.primary,
              color: palette.primary,
            }}
          >
            No points
          </span>
        </div>
      )}
    </div>
  );
};

export default EditPageBranding;
